import type { PageAdType } from '@/modules/adv/types'

/**
 * Placements that exist as a normal/fullscreen pair: the two describe the same spot in the two game
 * layouts, so exactly one of them may run at a time. Everything else — the in-game stickies above
 * all — has a single variant that belongs on screen in both modes.
 */
const FULLSCREEN_PAIRED_PLACEMENTS: Partial<Record<PageAdType, boolean>> = {
    sidebar: false,
    sidebar_bottom: false,
    sidebar_fullscreen: true,
    sidebar_bottom_fullscreen: true,
}

const FALLBACK_PLACEMENTS: Partial<Record<PageAdType, boolean>> = {
    sticky_mobile: true,
    sticky_mobile_non_game_page: true,
}

export function matchesFullscreenMode(placement: PageAdType, isFullscreen: boolean): boolean {
    const belongsToFullscreen = FULLSCREEN_PAIRED_PLACEMENTS[placement]
    return belongsToFullscreen === undefined || belongsToFullscreen === isFullscreen
}

export function hasFallbackCreative(placement: PageAdType): boolean {
    return !!FALLBACK_PLACEMENTS[placement]
}

export function hasRenderedBox(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
}

export function isElementCovered(el: HTMLElement): boolean {
    if (typeof document.elementFromPoint !== 'function') {
        return false
    }

    const rect = el.getBoundingClientRect()
    const left = Math.max(rect.left, 0)
    const top = Math.max(rect.top, 0)
    const right = Math.min(rect.right, window.innerWidth)
    const bottom = Math.min(rect.bottom, window.innerHeight)

    if (right <= left || bottom <= top) {
        return false
    }

    const points: [number, number][] = [
        [(left + right) / 2, (top + bottom) / 2],
        [left + 1, top + 1],
        [right - 1, bottom - 1],
    ]

    return points.some(([x, y]) => {
        const hit = document.elementFromPoint(x, y)
        return !hit || !el.contains(hit)
    })
}
